import { type SearchPosting, type SearchPostingsQuery, searchQueryString } from "../catalogue";
import { getJson, pathSegment, postJson } from "../client";
import { RESUME_BASE } from "./base";

/** A tailored resume's link, and how the person's resume fits postings. */

export interface TailoredResumeLink {
    resume_id: string;
    /** Opens the PDF until `expires_at`. */
    url: string;
    expires_at: string;
    /** The PDF's SHA-256, lower case hex. */
    sha256: string;
    byte_size: number;
}

/** A short lived signed link to one of the caller's tailored resumes, with its hash. */
export async function getTailoredLink(resumeId: string): Promise<TailoredResumeLink> {
    return getJson(`${RESUME_BASE}/tailored/${pathSegment(resumeId)}`);
}

/** A keyword a posting asks for that the resume does not say. */
export interface MissingKeyword {
    term: string;
    /** Whether the posting requires it or would only like it. */
    required: boolean;
}

/**
 * How the person's current resume reads against one posting, as the
 * envoy's matcher scores it. The fit is `covered` over `asked`; a posting
 * not read yet asks for 0.
 */
export interface PostingFit {
    posting_id: string;
    asked: number;
    covered: number;
    /** Whole percent, absent when the posting asks for nothing. */
    score?: number | null;
    covered_keywords: string[];
    missing: MissingKeyword[];
}

export interface FitList {
    version: number;
    fits: PostingFit[];
    not_found: string[];
}

export interface BestFit {
    posting: SearchPosting;
    fit: PostingFit;
}

export interface BestFitList {
    version: number;
    /** How many of the newest matches were scored, at most 200. */
    considered: number;
    /** Whether the search matched more than were scored. */
    more: boolean;
    results: BestFit[];
}

/** One page of every match of a search, ranked for the person's resume. */
export interface MostKeywordsPage {
    version: number;
    /** How many matches were ranked: all of them, up to 10,000. */
    total: number;
    /** Whether the search matched more than were ranked. The rest are the oldest. */
    more: boolean;
    /** Null on the last page. */
    next_cursor?: string | null;
    results: BestFit[];
}

/** The most postings one fit request names. */
export const MAX_FIT_POSTINGS = 50;

/** The current resume scored against up to 50 postings, in the order asked. 404 with no resume. */
export function fitPostings(postingIds: readonly string[]): Promise<FitList> {
    return postJson(`${RESUME_BASE}/fit`, { posting_ids: postingIds });
}

/**
 * A catalogue search's newest 200 matches, the best fit first. The sort
 * and cursor are the route's own; `limit` is how many come back.
 */
export function bestFit(query: SearchPostingsQuery): Promise<BestFitList> {
    return getJson(
        `${RESUME_BASE}/best-fit${searchQueryString({ ...query, sort: undefined, next_cursor: undefined })}`,
    );
}

/**
 * One page of every match of a search, the most keywords covered first.
 * 404 with no resume, and 429 when the catalogue is too busy to read it.
 */
export function mostKeywords(
    query: SearchPostingsQuery,
    cursor: string | undefined,
): Promise<MostKeywordsPage> {
    return getJson(
        `${RESUME_BASE}/most-keywords${searchQueryString({ ...query, sort: undefined, next_cursor: cursor })}`,
    );
}
